/**
 * GW SEARCH RESULTS
 *
 * Behaviour for sections/gw-search-results.liquid (the /search page):
 * - filters & sorting applied without a page reload (Section Rendering API), URL kept in sync with pushState
 * - dual price slider synced with the min / max inputs
 * - collapsible filter blocks, desktop sort dropdown, mobile filters modal, mobile view-mode toggles
 * - infinite scroll (next page fetched when the sentinel becomes visible)
 * - lazy "Pages" tab count
 */
(function () {
  'use strict';

  var root = document.getElementById('gwsr');
  if (!root) return;

  var searchUrl = root.dataset.searchUrl || '/search';
  var sectionId = root.dataset.sectionId;
  var countSectionId = root.dataset.countSectionId || 'gw-instant-search-count';
  var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var state = {
    controller: null,
    priceTimer: null,
    collapsed: {}, // filter param -> true when the block is collapsed
    modalOpen: false,
    modalScroll: 0,
    viewMode: null,
    loadingMore: false,
    observer: null
  };

  try {
    state.viewMode = window.localStorage.getItem('gwsr:view-mode');
  } catch (e) {
    state.viewMode = null;
  }

  /* ------------------------------------------------------------------------------------------------ helpers */

  function form() {
    return root.querySelector('[data-gwsr-form]');
  }

  function buildParams() {
    var params = new URLSearchParams();
    var seen = {};
    var data = new FormData(form());
    data.forEach(function (value, key) {
      if (value === '' || value === null) return;
      // The sidebar and the mobile modal carry the same inputs: only send each key/value pair once
      var pair = key + '=' + value;
      if (seen[pair]) return;
      seen[pair] = true;
      params.append(key, value);
    });
    return params;
  }

  function currentUrl(params) {
    return searchUrl + '?' + params.toString();
  }

  function setLoading(flag) {
    root.classList.toggle('gwsr--loading', !!flag);
  }

  function parseSection(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.getElementById('gwsr');
  }

  /* ------------------------------------------------------------------------------------------------ rendering */

  function applyViewMode() {
    var grid = root.querySelector('[data-gwsr-grid]');
    if (!grid) return;
    var mode = state.viewMode || 'grid';
    grid.classList.remove('gwsr-view--grid', 'gwsr-view--list', 'gwsr-view--row');
    grid.classList.add('gwsr-view--' + mode);
    root.querySelectorAll('[data-gwsr-view-mode]').forEach(function (button) {
      var active = button.dataset.gwsrViewMode === mode;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function applyCollapsedState() {
    root.querySelectorAll('[data-gwsr-filter-block]').forEach(function (block) {
      var key = block.dataset.gwsrFilterBlock;
      var title = block.querySelector('[data-gwsr-toggle]');
      var list = block.querySelector('.gwsr-filters-list');
      if (!title || !list) return;
      var collapsed = !!state.collapsed[key];
      title.classList.toggle('open', !collapsed);
      title.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      list.hidden = collapsed;
    });
  }

  function syncSliders() {
    root.querySelectorAll('[data-gwsr-price-range]').forEach(function (range) {
      var max = parseFloat(range.dataset.max) || 0;
      var minSlider = range.querySelector('.gwsr-slider-input--min');
      var maxSlider = range.querySelector('.gwsr-slider-input--max');
      var minInput = range.querySelector('[data-gwsr-price-input="min"]');
      var maxInput = range.querySelector('[data-gwsr-price-input="max"]');
      var bar = range.querySelector('.gwsr-slider-selected-bar');
      if (!minSlider || !maxSlider) return;
      var lo = minInput.value === '' ? 0 : Math.max(0, Math.min(max, parseFloat(minInput.value) || 0));
      var hi = maxInput.value === '' ? max : Math.max(0, Math.min(max, parseFloat(maxInput.value) || max));
      if (lo > hi) lo = hi;
      minSlider.value = lo;
      maxSlider.value = hi;
      if (bar && max > 0) {
        bar.style.left = (lo / max) * 100 + '%';
        bar.style.right = (100 - (hi / max) * 100) + '%';
      }
    });
  }

  function afterRender() {
    applyViewMode();
    applyCollapsedState();
    syncSliders();
    observeSentinel();
    if (state.modalOpen) {
      openModal(true);
    }
  }

  function replaceContent(newRoot) {
    var modalBody = root.querySelector('.gwsr-modal-body');
    state.modalScroll = modalBody ? modalBody.scrollTop : 0;
    root.innerHTML = newRoot.innerHTML;
    root.className = newRoot.className;
    afterRender();
  }

  /* ------------------------------------------------------------------------------------------------ fetching */

  function applyFilters(options) {
    options = options || {};
    var params = buildParams();
    var url = currentUrl(params);

    if (state.controller) state.controller.abort();
    state.controller = new AbortController();
    setLoading(true);

    var fetchUrl = url + '&section_id=' + encodeURIComponent(sectionId);
    fetch(fetchUrl, { signal: state.controller.signal, headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('gwsr ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var newRoot = parseSection(html);
        if (!newRoot) throw new Error('gwsr: section not found in response');
        replaceContent(newRoot);
        setLoading(false);
        if (!options.skipHistory) {
          window.history.pushState({ gwsr: true }, '', url);
        }
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        setLoading(false);
        if (window.console) console.warn('[gw-search-results]', error);
        // Fall back to a normal navigation so the user still gets results
        window.location.href = url;
      });
  }

  function loadNextPage(sentinel) {
    var nextUrl = sentinel.dataset.gwsrNextUrl;
    if (!nextUrl || state.loadingMore) return;
    state.loadingMore = true;
    sentinel.classList.add('gwsr-loading');
    var separator = nextUrl.indexOf('?') === -1 ? '?' : '&';
    fetch(nextUrl + separator + 'section_id=' + encodeURIComponent(sectionId), { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('gwsr next ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var newRoot = parseSection(html);
        var currentList = root.querySelector('[data-gwsr-results]');
        var newList = newRoot && newRoot.querySelector('[data-gwsr-results]');
        var pagination = root.querySelector('[data-gwsr-pagination]');
        var newPagination = newRoot && newRoot.querySelector('[data-gwsr-pagination]');
        if (currentList && newList) {
          Array.prototype.slice.call(newList.children).forEach(function (child) {
            currentList.appendChild(document.importNode(child, true));
          });
        }
        if (pagination && newPagination) {
          pagination.replaceWith(document.importNode(newPagination, true));
        } else if (pagination) {
          pagination.innerHTML = '';
        }
        state.loadingMore = false;
        observeSentinel();
      })
      .catch(function (error) {
        state.loadingMore = false;
        sentinel.classList.remove('gwsr-loading');
        if (window.console) console.warn('[gw-search-results]', error);
      });
  }

  function observeSentinel() {
    if (state.observer) state.observer.disconnect();
    var sentinel = root.querySelector('[data-gwsr-next-url]');
    if (!sentinel) return;
    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) loadNextPage(entry.target);
          });
        },
        { rootMargin: '600px 0px' }
      );
      state.observer.observe(sentinel);
    }
    checkSentinel();
  }

  // Scroll fallback (also covers browsers / states where IntersectionObserver callbacks are throttled)
  function checkSentinel() {
    var sentinel = root.querySelector('[data-gwsr-next-url]');
    if (!sentinel || state.loadingMore) return;
    var rect = sentinel.getBoundingClientRect();
    if (rect.top < window.innerHeight + 600) loadNextPage(sentinel);
  }

  var scrollRaf = null;
  window.addEventListener(
    'scroll',
    function () {
      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(function () {
        scrollRaf = null;
        checkSentinel();
      });
    },
    { passive: true }
  );

  function loadTabCounts() {
    var type = root.dataset.type;
    var terms = root.dataset.terms || '';
    if (terms === '') return;
    var otherType = type === 'product' ? 'page' : 'product';
    var target = root.querySelector('[data-gwsr-tab-total="' + otherType + '"]');
    if (!target || target.textContent.trim() !== '') return;
    var typeParam = otherType === 'page' ? 'page,article' : 'product';
    fetch(rootPath + 'search?type=' + typeParam + '&q=' + encodeURIComponent(terms) + '&section_id=' + encodeURIComponent(countSectionId), { headers: { Accept: 'text/html' } })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-gwis-results-count]');
        if (node) target.textContent = node.textContent.trim();
      })
      .catch(function () {});
  }

  // No keyword matches: if Search & Discovery's predictive search still finds the term (typos, plain-English queries
  // such as "gift for dad"), swap in the "closest products" view (this section rendered for the plain, unquoted query).
  // When the predictive search finds nothing either (nonsense words), the "nothing found" message stays.
  function loadClosestMatches() {
    var empty = root.querySelector('[data-gwsr-closest]');
    if (!empty) return;
    var terms = empty.dataset.gwsrClosest;
    var suggestUrl =
      rootPath +
      'search/suggest.json?q=' +
      encodeURIComponent(terms) +
      '&resources[type]=product&resources[limit]=1' +
      '&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,tag,body';
    fetch(suggestUrl, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        var products = data && data.resources && data.resources.results && data.resources.results.products;
        if (!products || products.length === 0) return '';
        return fetch(searchUrl + '?type=product&q=' + encodeURIComponent(terms) + '&section_id=' + encodeURIComponent(sectionId), {
          headers: { Accept: 'text/html' }
        }).then(function (response) {
          return response.ok ? response.text() : '';
        });
      })
      .then(function (html) {
        if (!html || !empty.isConnected) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var closest = doc.querySelector('[data-gwsr-closest-results] .gwsr-closest-heading');
        if (!closest) return;
        var wrapper = closest.parentNode;
        var fragment = document.createDocumentFragment();
        Array.prototype.slice.call(wrapper.children).forEach(function (child) {
          fragment.appendChild(document.importNode(child, true));
        });
        empty.replaceWith(fragment);
        var header = root.querySelector('.gwsr-results-header');
        if (header) header.textContent = '';
        var productsTotal = root.querySelector('[data-gwsr-tab-total="product"]');
        if (productsTotal) productsTotal.textContent = '';
      })
      .catch(function () {});
  }

  /* ------------------------------------------------------------------------------------------------ modal */

  function openModal(restore) {
    var modal = root.querySelector('[data-gwsr-modal]');
    if (!modal) return;
    state.modalOpen = true;
    modal.hidden = false;
    document.documentElement.classList.add('gwsr-modal-open');
    var button = root.querySelector('[data-gwsr-open-modal]');
    if (button) button.setAttribute('aria-expanded', 'true');
    var body = modal.querySelector('.gwsr-modal-body');
    if (body && restore) body.scrollTop = state.modalScroll;
    if (!restore) {
      var focusable = modal.querySelector('button, input');
      if (focusable) focusable.focus({ preventScroll: true });
    }
  }

  function closeModal() {
    var modal = root.querySelector('[data-gwsr-modal]');
    state.modalOpen = false;
    document.documentElement.classList.remove('gwsr-modal-open');
    if (modal) modal.hidden = true;
    var button = root.querySelector('[data-gwsr-open-modal]');
    if (button) {
      button.setAttribute('aria-expanded', 'false');
      button.focus({ preventScroll: true });
    }
  }

  /* ------------------------------------------------------------------------------------------------ events */

  root.addEventListener('click', function (event) {
    var target = event.target;

    // Collapsible filter blocks
    var toggle = target.closest('[data-gwsr-toggle]');
    if (toggle) {
      var block = toggle.closest('[data-gwsr-filter-block]');
      var list = toggle.parentElement.querySelector('.gwsr-filters-list, .gwsr-sort-options');
      var isOpen = toggle.classList.contains('open');
      toggle.classList.toggle('open', !isOpen);
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      if (list) list.hidden = isOpen;
      if (block) state.collapsed[block.dataset.gwsrFilterBlock] = isOpen;
      return;
    }

    // Sort dropdown (desktop)
    var sortButton = target.closest('.gwsr-sort-button');
    if (sortButton) {
      var dropdown = sortButton.closest('[data-gwsr-sort]');
      var open = dropdown.classList.toggle('open');
      sortButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    var sortOption = target.closest('[data-gwsr-sort-value]');
    if (sortOption) {
      var input = root.querySelector('[data-gwsr-sort-input]');
      if (input) input.value = sortOption.dataset.gwsrSortValue;
      applyFilters();
      return;
    }

    // View modes (mobile)
    var viewButton = target.closest('[data-gwsr-view-mode]');
    if (viewButton) {
      state.viewMode = viewButton.dataset.gwsrViewMode;
      try {
        window.localStorage.setItem('gwsr:view-mode', state.viewMode);
      } catch (e) {
        /* ignore */
      }
      applyViewMode();
      return;
    }

    // Mobile modal
    if (target.closest('[data-gwsr-open-modal]')) {
      openModal(false);
      return;
    }
    if (target.closest('[data-gwsr-close-modal]')) {
      closeModal();
      return;
    }
    if (target.closest('[data-gwsr-reset]')) {
      root.querySelectorAll('.gwsr-filters-checkbox').forEach(function (checkbox) {
        checkbox.checked = false;
      });
      root.querySelectorAll('[data-gwsr-price-input]').forEach(function (priceInput) {
        priceInput.value = '';
      });
      syncSliders();
      applyFilters();
      return;
    }
  });

  root.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      var dropdown = root.querySelector('[data-gwsr-sort].open');
      if (dropdown) {
        dropdown.classList.remove('open');
        return;
      }
      if (state.modalOpen) closeModal();
    }
    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-gwsr-toggle]')) {
      event.preventDefault();
      event.target.click();
    }
  });

  // Close the sort dropdown on outside click
  document.addEventListener('click', function (event) {
    var dropdown = root.querySelector('[data-gwsr-sort].open');
    if (dropdown && !dropdown.contains(event.target)) {
      dropdown.classList.remove('open');
      var button = dropdown.querySelector('.gwsr-sort-button');
      if (button) button.setAttribute('aria-expanded', 'false');
    }
  });

  root.addEventListener('change', function (event) {
    var target = event.target;

    if (target.matches('.gwsr-filters-checkbox')) {
      // Keep sidebar and modal copies of the same checkbox in sync
      root.querySelectorAll('.gwsr-filters-checkbox[name="' + CSS.escape(target.name) + '"][value="' + CSS.escape(target.value) + '"]').forEach(function (checkbox) {
        checkbox.checked = target.checked;
      });
      applyFilters();
      return;
    }

    if (target.matches('.gwsr-slider-input')) {
      var range = target.closest('[data-gwsr-price-range]');
      var minInput = range.querySelector('[data-gwsr-price-input="min"]');
      var maxInput = range.querySelector('[data-gwsr-price-input="max"]');
      var max = parseFloat(range.dataset.max) || 0;
      var lo = parseFloat(range.querySelector('.gwsr-slider-input--min').value);
      var hi = parseFloat(range.querySelector('.gwsr-slider-input--max').value);
      minInput.value = lo <= 0 ? '' : lo;
      maxInput.value = hi >= max ? '' : hi;
      syncPriceCopies(minInput, maxInput);
      syncSliders();
      applyFilters();
      return;
    }

    if (target.matches('[data-gwsr-price-input]')) {
      var rangeEl = target.closest('[data-gwsr-price-range]');
      syncPriceCopies(rangeEl.querySelector('[data-gwsr-price-input="min"]'), rangeEl.querySelector('[data-gwsr-price-input="max"]'));
      syncSliders();
      applyFilters();
    }
  });

  root.addEventListener('input', function (event) {
    var target = event.target;
    if (target.matches('.gwsr-slider-input')) {
      // Live-update the number fields and the selected bar while dragging
      var range = target.closest('[data-gwsr-price-range]');
      var minSlider = range.querySelector('.gwsr-slider-input--min');
      var maxSlider = range.querySelector('.gwsr-slider-input--max');
      var lo = parseFloat(minSlider.value);
      var hi = parseFloat(maxSlider.value);
      if (lo > hi) {
        if (target === minSlider) minSlider.value = hi;
        else maxSlider.value = lo;
        lo = parseFloat(minSlider.value);
        hi = parseFloat(maxSlider.value);
      }
      var max = parseFloat(range.dataset.max) || 0;
      range.querySelector('[data-gwsr-price-input="min"]').value = lo <= 0 ? '' : lo;
      range.querySelector('[data-gwsr-price-input="max"]').value = hi >= max ? '' : hi;
      var bar = range.querySelector('.gwsr-slider-selected-bar');
      if (bar && max > 0) {
        bar.style.left = (lo / max) * 100 + '%';
        bar.style.right = (100 - (hi / max) * 100) + '%';
      }
      return;
    }
    if (target.matches('[data-gwsr-price-input]')) {
      clearTimeout(state.priceTimer);
      state.priceTimer = setTimeout(function () {
        syncSliders();
      }, 150);
    }
  });

  function syncPriceCopies(minInput, maxInput) {
    root.querySelectorAll('[data-gwsr-price-input="min"]').forEach(function (el) {
      el.value = minInput.value;
    });
    root.querySelectorAll('[data-gwsr-price-input="max"]').forEach(function (el) {
      el.value = maxInput.value;
    });
  }

  // Browser back / forward: re-render from the URL
  window.addEventListener('popstate', function () {
    var url = window.location.pathname + window.location.search;
    if (state.controller) state.controller.abort();
    state.controller = new AbortController();
    setLoading(true);
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    fetch(url + separator + 'section_id=' + encodeURIComponent(sectionId), { signal: state.controller.signal })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var newRoot = parseSection(html);
        if (newRoot) replaceContent(newRoot);
        setLoading(false);
      })
      .catch(function () {
        setLoading(false);
      });
  });

  /* ------------------------------------------------------------------------------------------------ init */

  afterRender();
  loadTabCounts();
  loadClosestMatches();
})();
